import { MagnifyingGlassIcon } from '@heroicons/react/16/solid'
import type React from 'react'
import { useState } from 'react'
import { Button } from '../catalyst/button'
import { Heading } from '../catalyst/heading'
import { Input, InputGroup } from '../catalyst/input'
import { Select } from '../catalyst/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../catalyst/table'
import { Text } from '../catalyst/text'
import { useCatalogue, useDebounced } from '../lib/catalogue'
import { Shell } from './shell'

export type Filter = { param: string; label: string; options: { value: string; label: string }[]; initial?: string }

export type Column<T> = { header: string; cell: (row: T) => React.ReactNode; primary?: boolean }

const boxClass = 'rounded-lg border border-dashed p-10 text-center'

/** A searchable, cursor-paginated table over one list endpoint. The shared body of every admin list page. */
export function ListPage<T extends { id: string }>({
  title,
  description,
  path,
  noun,
  columns,
  rowHref,
  filter,
}: {
  title: string
  description: string
  path: string
  noun: string
  columns: Column<T>[]
  rowHref?: (row: T) => string
  /** One dropdown that narrows the list server-side (e.g. by review state). */
  filter?: Filter
}) {
  const [query, setQuery] = useState('')
  const debounced = useDebounced(query)
  const [filterValue, setFilterValue] = useState(filter?.initial ?? '')
  const { items, loading, loadingMore, error, hasMore, loadMore } = useCatalogue<T>(path, debounced, noun, filter ? { [filter.param]: filterValue } : {})

  return (
    <Shell>
      <Heading>{title}</Heading>
      <Text>{description}</Text>

      <div className="mt-6 flex max-w-2xl flex-wrap gap-3">
        <div className="min-w-64 flex-1">
        <InputGroup>
          <MagnifyingGlassIcon data-slot="icon" />
          <Input type="search" placeholder={`Search ${noun}…`} value={query} onChange={(event) => setQuery(event.target.value)} aria-label={`Search ${noun}`} />
        </InputGroup>
        </div>
        {filter && (
          <Select aria-label={filter.label} value={filterValue} onChange={(event) => setFilterValue(event.target.value)} className="max-w-56">
            {filter.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        )}
      </div>

      <div className="mt-8">
        {error ? (
          <div className={`${boxClass} border-red-300 dark:border-red-900`}>
            <Text>{error}</Text>
          </div>
        ) : loading ? (
          <div className={`${boxClass} border-zinc-950/15 dark:border-white/15`}>
            <Text>Loading {noun}…</Text>
          </div>
        ) : items.length === 0 ? (
          <div className={`${boxClass} border-zinc-950/15 dark:border-white/15`}>
            <Text>{debounced ? `Nothing matches "${debounced}".` : `No ${noun} found.`}</Text>
          </div>
        ) : (
          <>
            <Table striped>
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableHeader key={c.header}>{c.header}</TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id} href={rowHref?.(row)}>
                    {columns.map((c) => (
                      <TableCell key={c.header} className={c.primary ? 'font-medium text-zinc-950 dark:text-white' : undefined}>
                        {c.cell(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {hasMore && !debounced && (
              <div className="mt-4 flex justify-center">
                <Button outline disabled={loadingMore} onClick={loadMore}>
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  )
}
